import React from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { LineChart } from "@mui/x-charts/LineChart";
import { getChartColors, getChartStructuralStyles } from "theme";
import { HtmlTooltip } from "components/HeatMap";
import { appendTooltipGlossary } from "components/reports/shared/tooltipGlossary";
import { getStatusChartColor } from "components/reports/charts/statusChartColors";

/**
 * ReportLineChart - Wrapper for MUI LineChart with consistent styling
 *
 * @param {string} title - Chart title
 * @param {Array} xAxisData - Array of x-axis values (e.g., dates, months)
 * @param {Array} series - Array of series configs: [{ data: [...], label: 'Revenue', color: '#...' }]
 * @param {number} height - Chart height in pixels
 * @param {boolean} showInPaper - Wrap in Paper component
 * @param {string} xAxisLabel - Label for x-axis
 * @param {string} yAxisLabel - Label for y-axis
 * @param {Function} valueFormatter - Function to format values
 * @param {boolean} showGrid - Show grid lines
 * @param {string} tooltipTitle - Optional tooltip title for info icon
 * @param {string} tooltipContent - Optional tooltip content/explanation
 */
const ReportLineChart = ({
  title,
  xAxisData = [],
  series = [],
  height = 400,
  showInPaper = true,
  xAxisLabel,
  yAxisLabel,
  valueFormatter = (value) => value?.toLocaleString(),
  showGrid = true,
  tooltipTitle,
  tooltipContent,
  yAxisConfigs,
  chartMargin,
  leftAxis,
  rightAxis,
  slotProps,
  onMarkClick,
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const resolvedTooltipContent = appendTooltipGlossary(tooltipContent);
  const legendVerticalPosition = slotProps?.legend?.position?.vertical;
  const resolvedMargin = chartMargin || { top: 20, right: 80, bottom: 50, left: 80 };
  const chartResolvedMargin =
    legendVerticalPosition === "bottom"
      ? {
          ...resolvedMargin,
          bottom: Math.max(resolvedMargin.bottom || 0, 110),
        }
      : resolvedMargin;
  const resolvedSlotProps =
    legendVerticalPosition === "bottom"
      ? {
          ...slotProps,
          legend: {
            itemGap: 16,
            ...slotProps?.legend,
            padding: Math.max(slotProps?.legend?.padding || 0, 12),
          },
        }
      : {
          ...slotProps,
          legend: { itemGap: 16, ...slotProps?.legend },
        };

  const visibleSeries = series.filter((s) =>
    (s.data || []).some((value) => Number(value) !== 0),
  );

  const getPointTotal = (dataIndex) =>
    visibleSeries.reduce(
      (sum, s) => sum + (Number(s?.data?.[dataIndex]) || 0),
      0,
    );

  const formatValueWithPercent = (rawValue, total, formatter) => {
    const numericValue = Number(rawValue) || 0;
    const formattedValue = formatter(rawValue);
    if (!Number.isFinite(total) || total <= 0) return `${formattedValue}`;
    const percentage = ((numericValue / total) * 100).toFixed(1);
    return `${formattedValue} (${percentage}%)`;
  };
  
  const chartContent = (
    <Box>
      {title && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
          {resolvedTooltipContent && (
            <HtmlTooltip
              title={
                <Box sx={{ p: 1, maxHeight: "400px", overflowY: "auto" }}>
                  {tooltipTitle && (
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {tooltipTitle}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {resolvedTooltipContent}
                  </Typography>
                </Box>
              }
              arrow
              placement="bottom-start"
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: theme.palette.primary.main,
                  cursor: "help",
                  opacity: 0.7,
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              />
            </HtmlTooltip>
          )}
        </Box>
      )}
      {xAxisData.length === 0 || visibleSeries.length === 0 ? (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.background.default,
            borderRadius: "8px",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      ) : (
        <LineChart
          xAxis={[
            {
              scaleType: "point",
              data: xAxisData,
              label: xAxisLabel,
            },
          ]}
          leftAxis={leftAxis}
          rightAxis={rightAxis}
          yAxis={
            yAxisConfigs || [
              {
                label: yAxisLabel,
                labelStyle: {
                  transform: "translateX(-10px)",
                },
              },
            ]
          }
          series={visibleSeries.map((s, index) => ({
            data: s.data,
            label: s.label,
            color:
              getStatusChartColor(s.label, theme) ||
              s.color || chartColors.series[index % chartColors.series.length],
            valueFormatter: (value, context) => {
              const pointTotal = getPointTotal(context?.dataIndex);
              const formatter = s.valueFormatter || valueFormatter;
              return formatValueWithPercent(value, pointTotal, formatter);
            },
            curve: s.curve || "linear",
            showMark: s.showMark !== false,
            yAxisId: s.yAxisId,
            area: s.area || false,
          }))}
          height={height}
          margin={chartResolvedMargin}
          grid={{ vertical: showGrid, horizontal: showGrid }}
          slotProps={resolvedSlotProps}
          onMarkClick={
            onMarkClick
              ? (event, d) => {
                  const item = xAxisData[d.dataIndex];
                  if (item !== undefined) onMarkClick(d.dataIndex, item);
                }
              : undefined
          }
          sx={{
            ...getChartStructuralStyles(theme),
            "& .MuiChartsLegend-mark": {
              rx: 2,
              ry: 2,
            },
            "& .MuiChartsLegend-series rect": {
              rx: 2,
              ry: 2,
            },
          }}
        />
      )}
    </Box>
  );

  if (showInPaper) {
    return (
      <Paper
        sx={{
          p: 3,
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.primary.border2}`,
          borderRadius: "12px",
        }}
      >
        {chartContent}
      </Paper>
    );
  }

  return chartContent;
};

export default ReportLineChart;
